import { AdminPanelSettingsOutlined, HelpOutline, LogoutOutlined, SettingsOutlined } from '@mui/icons-material'
import Avatar from '@mui/material/Avatar'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { type SxProps, type Theme, useTheme } from '@mui/material/styles'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import type { SyntheticEvent } from 'react'
import { Fragment, useId, useState } from 'react'

import { type Settings } from '@core/context/settingsContext'

import useIsAdmin from '~/auth/hooks/useIsAdmin'
import { useOverlay } from '~/components/Overlay'
import { useGateValue } from '~/configs/statsig'
import { useCurrentUser } from '~/data/api/hooks/users'
import useFormatters from '~/data/formatters'
import useInternalAdminTools from '~/features/internalAdminTools/useInternalAdminTools'
import { useAuth } from '~/hooks/useAuth'
import routeDirectory from '~/navigation/routes'

import HelpOverlay from './HelpOverlay'

type UserDropdownProps = {
  settings: Settings
  /**
   * When true, the trigger renders as a full-width row with avatar + name/email.
   * When false (default), it matches the existing avatar-only button used in the
   * collapsed sidebar and legacy callers.
   */
  expanded?: boolean
  /**
   * Controls where the menu opens relative to the trigger. The sidebar uses
   * `right-top` (up and to the right) when expanded and `right` when collapsed.
   */
  placement?: 'bottom' | 'right' | 'right-top'
}

export default function UserDropdown(props: UserDropdownProps) {
  const t = useTranslations('layouts.UserDropdown')
  const isAdmin = useIsAdmin()
  const isSynopInternal = useGateValue('synop_internal_only')
  const { enabled: internalAdminToolsEnabled, toggle: toggleInternalAdminTools } = useInternalAdminTools()
  const { settings, expanded = false, placement = 'bottom' } = props

  const [anchorEl, setAnchorEl] = useState<Element | null>(null)

  const router = useRouter()
  const { logout } = useAuth()
  const { palette } = useTheme()
  const { synopUser } = useCurrentUser()
  const format = useFormatters()

  const { direction } = settings

  const handleDropdownOpen = (event: SyntheticEvent) => {
    setAnchorEl(event.currentTarget)
  }

  const handleDropdownClose = (url?: string) => {
    if (url) {
      router.push(url)
    }
    setAnchorEl(null)
  }

  const handleLogout = () => {
    logout()
    handleDropdownClose()
  }

  // Synop-internal only. Label is deliberately untranslated, matching the
  // Cmd+K command and other internal-only tooling (`BackendSwitcher`).
  const handleToggleInternalAdminTools = () => {
    toggleInternalAdminTools()
    handleDropdownClose()
  }

  const initials = (synopUser?.name || '')
    .split(' ')
    .map((word) => word[0])
    .join('')

  // Plain shield on the avatar while tools are on, so the enabled state is
  // visible without opening the menu (menu/Cmd+K keep the admin-panel icon).
  const showInternalAdminBadge = isSynopInternal && internalAdminToolsEnabled

  const helpOverlay = useOverlay()

  const anchorOrigin =
    placement === 'right'
      ? { vertical: 'center' as const, horizontal: 'right' as const }
      : placement === 'right-top'
        ? { vertical: 'top' as const, horizontal: 'right' as const }
        : { vertical: 'bottom' as const, horizontal: direction === 'ltr' ? ('right' as const) : ('left' as const) }

  const transformOrigin =
    placement === 'right'
      ? { vertical: 'center' as const, horizontal: 'left' as const }
      : placement === 'right-top'
        ? { vertical: 'bottom' as const, horizontal: 'left' as const }
        : { vertical: 'top' as const, horizontal: direction === 'ltr' ? ('right' as const) : ('left' as const) }

  return (
    <Fragment>
      <HelpOverlay {...helpOverlay} />
      {expanded ? (
        <Box
          component="button"
          type="button"
          onClick={handleDropdownOpen}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchorEl)}
          aria-controls={anchorEl ? 'user-menu' : undefined}
          data-cy="user-menu-trigger"
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 1,
            py: 0.75,
            border: 0,
            borderRadius: 2,
            cursor: 'pointer',
            fontFamily: 'inherit',
            backgroundColor: 'transparent',
            transition: 'background-color .12s ease-in-out',
            textAlign: 'left',
            '&:hover': { backgroundColor: 'action.hover' }
          }}
        >
          <UserAvatar
            initials={initials}
            showInternalAdminBadge={showInternalAdminBadge}
            size={32}
            sx={{
              fontSize: '0.75rem',
              fontWeight: 700,
              backgroundColor: 'background.paper',
              border: 2,
              borderColor: 'text.primary'
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'text.primary',
                lineHeight: 1.3
              }}
            >
              {synopUser?.name}
            </Typography>
            <Typography
              noWrap
              sx={{
                fontSize: '0.6875rem',
                color: 'text.secondary',
                lineHeight: 1.3
              }}
            >
              {synopUser?.email}
            </Typography>
          </Box>
        </Box>
      ) : (
        <IconButton
          aria-label={t('userMenu')}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchorEl)}
          aria-controls={anchorEl ? 'user-menu' : undefined}
          onClick={handleDropdownOpen}
          data-cy="user-menu-trigger"
          sx={{ p: 0 }}
        >
          <UserAvatar
            initials={initials}
            showInternalAdminBadge={showInternalAdminBadge}
            sx={{ backgroundColor: 'background.paper', border: 2, borderColor: 'text.primary' }}
          />
        </IconButton>
      )}
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleDropdownClose()}
        sx={{
          '& .MuiMenu-paper': {
            width: 230,
            ...(placement === 'bottom' ? { mt: 4 } : { ml: 1 }),
            bgcolor: palette.background.navCard
          }
        }}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        MenuListProps={{
          onKeyDown: (e) => {
            if (e.key === 'Tab') {
              e.preventDefault()
              const items = Array.from(
                e.currentTarget.querySelectorAll('[role="menuitem"]:not([aria-disabled="true"])')
              ) as HTMLElement[]
              const currentIndex = items.indexOf(document.activeElement as HTMLElement)
              const nextIndex = e.shiftKey
                ? currentIndex > 0
                  ? currentIndex - 1
                  : items.length - 1
                : currentIndex < items.length - 1
                  ? currentIndex + 1
                  : 0
              items[nextIndex]?.focus()
            }
          }
        }}
      >
        <Box sx={{ pt: 2, pb: 3, px: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <UserAvatar
              initials={initials}
              showInternalAdminBadge={showInternalAdminBadge}
              sx={{ backgroundColor: 'background.navCard', border: 2, borderColor: 'text.primary' }}
            />
            <Box sx={{ display: 'flex', ml: 3, alignItems: 'flex-start', flexDirection: 'column' }}>
              <Typography sx={{ fontWeight: 600 }}>{synopUser?.name}</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>
                {format.userRole(synopUser?.roles?.[0])}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ mt: '0 !important' }} />
        {isSynopInternal && (
          <MenuItem
            onClick={handleToggleInternalAdminTools}
            data-cy="toggle-internal-admin-tools"
            sx={{ py: 2, '& svg': { mr: 2, fontSize: '1.375rem', color: 'text.primary' } }}
          >
            <AdminPanelSettingsOutlined />
            {internalAdminToolsEnabled ? 'Hide internal admin tools' : 'Show internal admin tools'}
          </MenuItem>
        )}
        {isSynopInternal && <Divider />}
        <MenuItem sx={{ p: 0 }} onClick={() => handleDropdownClose(routeDirectory.settings.root)}>
          <Box
            sx={{
              py: 2,
              px: 4,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              color: 'text.primary',
              textDecoration: 'none',
              '& svg': {
                mr: 2,
                fontSize: '1.375rem',
                color: 'text.primary'
              }
            }}
          >
            <SettingsOutlined />
            {t('settings')}
          </Box>
        </MenuItem>
        {isAdmin && (
          <MenuItem
            onClick={helpOverlay.openOverlay}
            sx={{ py: 2, '& svg': { mr: 2, fontSize: '1.375rem', color: 'text.primary' } }}
          >
            <HelpOutline />
            {t('getHelp')}
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={handleLogout}
          sx={{ py: 2, '& svg': { mr: 2, fontSize: '1.375rem', color: 'text.primary' } }}
        >
          <LogoutOutlined />
          {t('logout')}
        </MenuItem>
      </Menu>
    </Fragment>
  )
}

type UserAvatarProps = {
  initials: string
  showInternalAdminBadge: boolean
  size?: number
  sx?: SxProps<Theme>
}

/** Letter ink (`S`/`n`/`o`/`p`) from `public/images/logos/synop-logo.svg`. */
const SYNOP_LOGO_NAVY = '#263370'

/** Lightning-bolt gradient stops from the same logo (blue → cyan → pale). */
const SYNOP_LOGO_BOLT_GRADIENT = [
  { offset: '0%', color: '#3E77FE' },
  { offset: '48.83%', color: '#2EB7FA' },
  { offset: '100%', color: '#B1E4E4' }
] as const

/** Filled Material shield path (`@mui/icons-material/Shield`). */
const SHIELD_PATH = 'M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5z'

function UserAvatar({ initials, showInternalAdminBadge, size, sx }: UserAvatarProps) {
  const avatar = (
    <Avatar
      sx={{
        ...(size != null ? { width: size, height: size } : null),
        ...sx
      }}
    >
      {initials}
    </Avatar>
  )

  if (!showInternalAdminBadge) return avatar

  // Sized to read in the avatar corner without dominating the initials.
  const iconSize = size != null && size <= 32 ? 15 : 18

  return (
    <Badge
      overlap="circular"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      badgeContent={<SynopShieldBadge size={iconSize} />}
      sx={{
        '& .MuiBadge-badge': {
          p: 0,
          minWidth: 0,
          height: 'auto',
          bgcolor: 'transparent'
        }
      }}
    >
      {avatar}
    </Badge>
  )
}

function SynopShieldBadge({ size }: { size: number }) {
  // Multiple badges can mount at once (trigger + open menu); keep gradient ids unique.
  const gradientId = `synop-bolt-${useId().replace(/:/g, '')}`

  // Untranslated — matches the Cmd+K / profile-menu labels for this staff-only surface.
  return (
    <Tooltip title="Internal admin tools are enabled.">
      <Box
        component="span"
        data-testid="internal-admin-tools-badge"
        sx={(theme) => ({
          display: 'inline-flex',
          lineHeight: 0,
          // Halo so the badge clears the avatar border underneath.
          filter: `drop-shadow(0 0 1.5px ${theme.palette.background.paper})`
        })}
      >
        <Box
          component="svg"
          aria-hidden
          viewBox="0 0 24 24"
          sx={{ width: size, height: size, display: 'block' }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
              {SYNOP_LOGO_BOLT_GRADIENT.map((stop) => (
                <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
              ))}
            </linearGradient>
          </defs>
          <path
            d={SHIELD_PATH}
            fill={`url(#${gradientId})`}
            stroke={SYNOP_LOGO_NAVY}
            // Thinner than ShieldOutlined's built-in frame (~2 viewBox units).
            strokeWidth={1}
            strokeLinejoin="round"
          />
        </Box>
      </Box>
    </Tooltip>
  )
}

